import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Award, Mail, Phone, MessageCircle, MapPin, Instagram } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import { EnrollButtons } from "@/components/EnrollButtons";
import { Curriculum } from "@/components/Curriculum";
import { PricingCard } from "@/components/PricingCard";
import { FAQ } from "@/components/FAQ";
import { BatchInfo } from "@/components/BatchInfo";
import { ORIGINAL_PRICE, PRICE, DISCOUNT, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, INSTAGRAM_URL } from "@/lib/courseData";

const stats = [
  { label: "Modules", value: "5" },
  { label: "Discount", value: `${DISCOUNT}%`, accent: true },
  { label: "Level", value: "A–Z" },
];

const testimonials = [
  { name: "Aditya S.", role: "Retail Trader, Mumbai", quote: "The zone-marking workflow completely changed how I look at charts. I finally know where to enter and where to stop out." },
  { name: "Priya K.", role: "Part-time trader", quote: "The risk management module alone was worth the price. I've cut my drawdown by more than half in 3 months." },
  { name: "Rohan V.", role: "Full-time swing trader", quote: "Clear, no-fluff teaching. The candlestick lessons are the most useful concepts I've learned in 4 years." },
];

const Hero = () => (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0">
      <img src="/img/hero.jpg" alt="" className="w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/70 to-[#0A0A0A]" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-28 grid lg:grid-cols-12 gap-10 items-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7 space-y-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/70" data-testid="hero-badge">
          <span className="h-1.5 w-1.5 rounded-full bg-fin-green animate-pulse" />
          <span className="tracking-overline">Now enrolling — {DISCOUNT}% off</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white" data-testid="hero-title">
          Complete Stock Market Course <span className="text-fin-green">(A to Z)</span>
        </h1>
        <p className="text-white/70 text-base md:text-lg max-w-xl leading-relaxed">
          From market basics and candlestick patterns to institutional supply/demand zones, intraday & swing setups,
          and the risk management and psychology that keep you consistent.
        </p>
        <div className="flex items-baseline gap-3" data-testid="hero-price">
          <span className="font-mono text-4xl text-fin-green">₹{PRICE.toLocaleString("en-IN")}</span>
          <span className="font-mono text-lg text-white/40 line-through">₹{ORIGINAL_PRICE.toLocaleString("en-IN")}</span>
          <span className="bg-fin-green text-black text-[10px] font-bold tracking-overline px-2 py-1 rounded-sm">{DISCOUNT}% OFF</span>
        </div>
        <BatchInfo id="hero" />
        <EnrollButtons id="hero" />
        <div className="grid grid-cols-3 gap-4 pt-4 max-w-md">
          {stats.map((s, i) => (
            <div key={s.label} className="border-l border-white/10 pl-4">
              <div className={`font-mono text-2xl ${s.accent ? "text-fin-green" : "text-white"}`} data-testid={`stat-${i}`}>{s.value}</div>
              <div className="text-[10px] tracking-overline text-white/40 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
      <div className="lg:col-span-5 hidden lg:block rounded-lg overflow-hidden gg-border">
        <img src="/img/student.jpg" alt="Stock market charts" className="w-full h-[460px] object-cover" />
      </div>
    </div>
  </section>
);

const Mentor = () => (
  <section id="mentor" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center scroll-mt-20">
    <div className="relative max-w-md mx-auto lg:mx-0 w-full">
      <img src="/img/mentor.jpg" alt="RL Trading Institute mentor" data-testid="mentor-photo" className="w-full max-w-md mx-auto lg:mx-0 h-[520px] object-cover object-top rounded-lg border border-white/10" />
      <div className="absolute -bottom-6 -right-2 md:-right-6 bg-fin-green text-black px-5 py-4 rounded-sm">
        <div className="font-mono text-2xl leading-none">10+</div>
        <div className="text-[10px] tracking-overline mt-1">Years experience</div>
      </div>
    </div>
    <div className="space-y-6">
      <div className="tracking-overline text-[10px] text-fin-green">Meet the mentor</div>
      <h2 className="font-display text-3xl md:text-4xl text-white" data-testid="mentor-title">Trader/Mentor</h2>
      <p className="text-white/70 leading-relaxed">
        Over a decade of screen time in Indian equities and derivatives. RL built RL Trading Institute to package
        everything a serious trader needs into one structured path — from the very basics all the way to
        supply/demand execution and mindset training.
      </p>
      <ul className="space-y-3">
        {["10+ years trading Indian markets", "Institutional supply & demand specialist", "Focused on risk-first, high-quality setups", "500+ students mentored across India"].map((p) => (
          <li key={p} className="flex items-start gap-3 text-white/80 text-sm">
            <CheckCircle2 className="h-5 w-5 text-fin-green shrink-0 mt-0.5" /> {p}
          </li>
        ))}
      </ul>
      <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="mentor-instagram" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-fin-green border border-white/15 rounded-sm px-4 py-2.5 transition-colors">
        <Instagram className="h-4 w-4 text-fin-green" /> Follow @rlt_pune on Instagram
      </a>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
    <div className="tracking-overline text-[10px] text-fin-green mb-3">Student results</div>
    <h2 className="font-display text-3xl md:text-4xl text-white mb-12">Real feedback from real traders.</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {testimonials.map((t) => (
        <div key={t.name} className="p-6 border border-white/10 rounded-md bg-[#141414]" data-testid={`testimonial-${t.name}`}>
          <Award className="h-5 w-5 text-fin-green mb-4" />
          <p className="text-white/80 text-sm leading-relaxed">"{t.quote}"</p>
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="text-white font-semibold text-sm">{t.name}</div>
            <div className="text-white/60 text-xs">{t.role}</div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="max-w-7xl mx-auto px-6 lg:px-10 pb-12 scroll-mt-20">
    <div className="border border-white/10 rounded-lg bg-[#141414] p-8 md:p-12 grid md:grid-cols-2 gap-10">
      <div>
        <div className="tracking-overline text-[10px] text-fin-green mb-3">Get in touch</div>
        <h2 className="font-display text-3xl text-white mb-4">Have a question about the course?</h2>
        <p className="text-white/70 max-w-md mb-8">Reach out on WhatsApp or call — we'll share batch timings and enrollment details.</p>
        <EnrollButtons id="contact" />
      </div>
      <ul className="space-y-5 text-white/90 text-sm md:border-l md:border-white/10 md:pl-10">
        <li className="flex items-center gap-3"><Mail className="h-4 w-4 text-fin-green" /><a href="mailto:rltradinginstitute@gmail.com" className="hover:text-fin-green" data-testid="contact-email">rltradinginstitute@gmail.com</a></li>
        <li className="flex items-center gap-3"><Phone className="h-4 w-4 text-fin-green" /><a href={PHONE_TEL} className="hover:text-fin-green" data-testid="contact-phone">{PHONE_DISPLAY}</a></li>
        <li className="flex items-center gap-3"><MessageCircle className="h-4 w-4 text-fin-green" /><a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="hover:text-fin-green" data-testid="contact-whatsapp">Chat on WhatsApp</a></li>
        <li className="flex items-center gap-3"><Instagram className="h-4 w-4 text-fin-green" /><a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-fin-green" data-testid="contact-instagram">@rlt_pune</a></li>
        <li className="flex items-center gap-3"><MapPin className="h-4 w-4 text-fin-green" /> India</li>
      </ul>
    </div>
  </section>
);

const Landing = () => (
  <SiteLayout>
    <Hero />
    <Curriculum />
    <PricingCard />
    <Mentor />
    <Testimonials />
    <FAQ />
    <Contact />
  </SiteLayout>
);

export default Landing;
